/**
 * 화면 스타일 샘플 2 — 목록 조회(로컬 CRUD)
 * 조회바 높이·버튼 density·그리드를 감싼 카드가 type2에서 커진다.
 */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer, CommonCss } from 'styles/common.styles';
import { cssVar, theme } from 'styles/theme';
import { FormArea, CollapsibleSearchPanel, ActionButtonGroup } from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import { showAlert } from 'utils/toastBridge';
import { UI_CHROME_CATALOG, normalizeUiChromeType } from 'styles/uiChrome';
import { useErpThemeOptional } from 'contexts/ErpThemeContext';

const GRID_ID = 'adm-chrome-list';
const LABEL_W = 'var(--erp-form-search-label-width, 56px)';

const Banner = styled.div`
  flex-shrink: 0;
  padding: 10px 12px;
  border-radius: var(--erp-radius-panel, 8px);
  background: ${cssVar('color-bg-white', theme.colors.white)};
  border: 1px solid ${cssVar('color-border', theme.colors.border)};
  box-shadow: var(--erp-panel-shadow, none);
  font-size: ${theme.typography.fontSize.xs};
  color: ${cssVar('color-text-secondary', theme.colors.text.secondary)};
  line-height: 1.5;
`;

const GridCard = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: var(--erp-radius-panel, 8px);
  border: 1px solid ${cssVar('color-border', theme.colors.border)};
  background: ${cssVar('color-bg-white', theme.colors.white)};
  box-shadow: var(--erp-panel-shadow, none);
`;

const SEED = [
  { code: 'A01', name: '사무관리비', useYn: 'Y', sortOrder: 10, remark: '소모품' },
  { code: 'A02', name: '여비교통비', useYn: 'Y', sortOrder: 20, remark: '' },
  { code: 'A03', name: '공공요금', useYn: 'Y', sortOrder: 30, remark: '전기·통신' },
  { code: 'B01', name: '교육훈련비', useYn: 'N', sortOrder: 40, remark: '미사용' },
];

const ChromeListInner = () => {
  const gridRef = useRef(null);
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const erpTheme = useErpThemeOptional();
  const chromeId = normalizeUiChromeType(erpTheme?.resolvedAppearance?.uiChromeType);
  const chrome = UI_CHROME_CATALOG.find((x) => x.id === chromeId) || UI_CHROME_CATALOG[0];

  const searchDefaultValues = { tenantCode, keyword: '' };
  const methods = useForm({ mode: 'onSubmit', defaultValues: { ...searchDefaultValues } });
  const { getValues: getSearchValues, setValue: setSearchValue } = methods;

  useEffect(() => {
    setSearchValue('tenantCode', tenantCode);
  }, [tenantCode, setSearchValue]);

  const columns = useMemo(
    () => [
      hiCol.edTextColumnForKey({ header: '코드', field: 'code', size: 110, required: true }),
      hiCol.edTextColumn({ header: '명', field: 'name', size: 200, required: true }),
      hiCol.edCheckboxColumn({ header: '사용', field: 'useYn', size: 72 }),
      hiCol.edNumberColumn({ header: '정렬', field: 'sortOrder', size: 80 }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 220 }),
    ],
    []
  );

  const grid = useHiGridHook({
    gridRef,
    getSearchValues,
    enableContextMenu: true,
    autoSearch: false,
    defaultRow: { useYn: 'Y', sortOrder: 10 },
    customOnSearch: async () => gridRef.current?.getData?.() ?? [],
    customOnSave: async ({ setData }) => {
      const current = gridRef.current?.getData?.() ?? [];
      const next = current
        .filter((r) => r.mod !== 'D')
        .map(({ mod, _tempId, ...rest }) => rest);
      setData(next);
    },
  });

  useEffect(() => {
    grid.setData(SEED.map((r) => ({ ...r })));
    // 마운트 시 한 번만 시드
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getData = useCallback(
    () => gridRef.current?.getFilteredData?.() ?? gridRef.current?.getData?.() ?? [],
    []
  );
  const getDataColumns = useCallback(() => gridRef.current?.getDataColumns?.() ?? [], []);

  useRegisterGridExport({
    id: GRID_ID,
    label: '화면스타일 목록',
    getData,
    getDataColumns,
    order: 0,
  });
  const { excelButton, dialog: excelDialog } = useExcelExportAction({ fileName: '화면스타일목록' });

  const actionButtons = [
    {
      id: 'search',
      label: '조회',
      icon: '🔍',
      onClick: () => grid.handleSearch().catch((e) => showAlert(e.message || '조회 실패', 'error')),
      hotkey: 'F7',
    },
    { id: 'add', label: '추가', icon: '➕', onClick: () => grid.handleAdd(), hotkey: 'F8' },
    { id: 'delete', label: '삭제', icon: '➖', onClick: () => grid.handleDelete(), hotkey: 'F9' },
    {
      id: 'save',
      label: '저장',
      icon: '💾',
      onClick: () => grid.handleSave().catch((e) => showAlert(e.message || '저장 실패', 'error')),
      hotkey: 'F10',
    },
    excelButton,
  ];

  const searchFields = [
    [{ name: 'keyword', label: '검색어', width: CommonCss.w160, labelWidth: LABEL_W }],
  ];

  return (
    <PageContainer>
      <Banner>
        목록 샘플 · 현재 {chrome.label} ({chromeId}). type2로 바꾸면 조회 리본·F7~F10 버튼·입력칸이 커지고
        그리드가 둥근 카드 안에 들어갑니다. 단축키·CRUD 동작은 type1과 같습니다.
      </Banner>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} loading={grid.loading} />
        <CollapsibleSearchPanel storageKey={GRID_ID} prefsUserId={prefsUserId}>
          <FormArea
            fields={searchFields}
            compact
            onFieldEnter={() => grid.handleSearch()}
            defaultValues={searchDefaultValues}
          />
        </CollapsibleSearchPanel>
      </FormProvider>
      <GridExportFocusScope gridId={GRID_ID}>
        <GridCard>
          <HiGridComponent
            ref={gridRef}
            columns={columns}
            height="100%"
            showRowNumber
            rowNumberSize={56}
            enableColumnFilters
            enableContextMenu
            enableRowEditMenu
            enableCut
            enableFindReplace
            allowReplace
            enableHeaderMenu
            enableSelectionStats
            enableFreeze
            enableQuickExport
            showModColumn
            exportFileName="화면스타일목록"
            gridId={GRID_ID}
            prefsUserId={prefsUserId}
            {...grid.contextMenuProps}
          />
        </GridCard>
      </GridExportFocusScope>
      {excelDialog}
    </PageContainer>
  );
};

const ChromeListSample = () => (
  <GridExportProvider>
    <ChromeListInner />
  </GridExportProvider>
);

export default ChromeListSample;
